/* External */
import {
  memo, useMemo, useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { Set } from 'immutable';

/* Ott-common */
import { EntityName } from '@dtvgo/backend-service/normalize';

/* Components */
import SwimlaneCarouselContainer from '../SwimlaneCarousel';
import ScrollIntoViewIfFocused from '../ScrollIntoViewIfFocused';
import UpSellBanner from '../UpSellBanner';
import HomeBannerSlot from '../HomeBannerSlot/HomeBannerSlot';
import ToastBannerSlot from '../ToastBannerSlot/ToastBannerSlot';

/* Utils */
import { useItemData } from '../VirtualizedList';
import { ScrollPosition } from '../ScrollView';
import { BannerType } from '../../utils/cardsUtils';
import { PageType } from '../../utils/constants';
import { getIsCurableBannerActive } from '../../utils/functions';
import {
  isLanderBannerActive,
  isRowAdsBannerActive,
  isToastBannerActive,
} from '../../utils/env';
import {
  isToastBannerRowType,
  getCarouselRowBannerId,
  readCarouselRowType,
  isHomeBannerRowType,
  isCommunicationBannerFetchEnabledForRowType,
} from '../../utils/landerCommunicationHelpers';

import './LanderHeroStack.scss';

function ToastBannerRowSkip({ onFetched }) {
  useEffect(() => {
    onFetched();
  }, [onFetched]);
  return undefined;
}

ToastBannerRowSkip.propTypes = {
  onFetched: PropTypes.func.isRequired,
};

function LanderHeroStackWithAboveToast({
  homeCarouselId,
  toastCarouselId,
  onHomeFetched,
  onToastFetched,
  toastFetchEnabled,
  homeBannerFetchEnabled,
  registerCommunicationUnavailable,
  onBecameFocused = undefined,
  stealFocusByDefault = false,
  toastUnavailable = false,
}) {
  return (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <div className="dtv-html5-c-lander-hero-stack">
        <ToastBannerSlot
          carouselId={toastCarouselId}
          focusKey={`toast-banner-${toastCarouselId}`}
          fetchEnabled={toastFetchEnabled}
          onFetched={onToastFetched}
          onUnavailable={registerCommunicationUnavailable}
          onBecameFocused={onBecameFocused}
          stealFocusByDefault={stealFocusByDefault && !toastUnavailable}
          inLanderHeroStack
        />
        <HomeBannerSlot
          carouselId={homeCarouselId}
          focusKey={`home-banner-${homeCarouselId}`}
          fetchEnabled={homeBannerFetchEnabled}
          onFetched={onHomeFetched}
          onUnavailable={registerCommunicationUnavailable}
          onBecameFocused={onBecameFocused}
          stealFocusByDefault={stealFocusByDefault && toastUnavailable}
        />
      </div>
    </ScrollIntoViewIfFocused>
  );
}

LanderHeroStackWithAboveToast.propTypes = {
  homeCarouselId: PropTypes.string.isRequired,
  toastCarouselId: PropTypes.string.isRequired,
  onHomeFetched: PropTypes.func.isRequired,
  onToastFetched: PropTypes.func.isRequired,
  toastFetchEnabled: PropTypes.bool.isRequired,
  homeBannerFetchEnabled: PropTypes.bool.isRequired,
  registerCommunicationUnavailable: PropTypes.func.isRequired,
  onBecameFocused: PropTypes.func,
  stealFocusByDefault: PropTypes.bool,
  toastUnavailable: PropTypes.bool,
};

function ToastBannerRowContent({
  bannerCarouselId,
  prevCarouselInfo = undefined,
  nextCarouselInfo = undefined,
  nextBannerCarouselId,
  onToastFetched,
  onNextHomeFetched,
  toastFetchEnabled,
  homeBannerFetchEnabled,
  registerCommunicationUnavailable,
  skipEarlierDuplicateToastSlot,
  stealFocusByDefault = false,
  onBecameFocused = undefined,
  toastUnavailable = false,
}) {
  if (skipEarlierDuplicateToastSlot) {
    return <ToastBannerRowSkip onFetched={onToastFetched} />;
  }
  if (isHomeBannerRowType(readCarouselRowType(nextCarouselInfo))) {
    return (
      <LanderHeroStackWithAboveToast
        homeCarouselId={nextBannerCarouselId}
        toastCarouselId={bannerCarouselId}
        onHomeFetched={onNextHomeFetched}
        onToastFetched={onToastFetched}
        toastFetchEnabled={toastFetchEnabled}
        homeBannerFetchEnabled={homeBannerFetchEnabled}
        registerCommunicationUnavailable={registerCommunicationUnavailable}
        onBecameFocused={onBecameFocused}
        stealFocusByDefault={stealFocusByDefault}
        toastUnavailable={toastUnavailable}
      />
    );
  }
  if (isHomeBannerRowType(readCarouselRowType(prevCarouselInfo))) {
    // eslint-disable-next-line unicorn/no-null -- React requires null for empty render
    return null;
  }
  return (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <ToastBannerSlot
        carouselId={bannerCarouselId}
        focusKey={`toast-banner-${bannerCarouselId}`}
        fetchEnabled={toastFetchEnabled}
        onFetched={onToastFetched}
        onUnavailable={registerCommunicationUnavailable}
        stealFocusByDefault={stealFocusByDefault}
        onBecameFocused={onBecameFocused}
      />
    </ScrollIntoViewIfFocused>
  );
}

ToastBannerRowContent.propTypes = {
  bannerCarouselId: PropTypes.string.isRequired,
  prevCarouselInfo: PropTypes.shape({ type: PropTypes.string }),
  nextCarouselInfo: PropTypes.shape({ type: PropTypes.string }),
  nextBannerCarouselId: PropTypes.string.isRequired,
  onToastFetched: PropTypes.func.isRequired,
  onNextHomeFetched: PropTypes.func.isRequired,
  toastFetchEnabled: PropTypes.bool.isRequired,
  homeBannerFetchEnabled: PropTypes.bool.isRequired,
  registerCommunicationUnavailable: PropTypes.func.isRequired,
  skipEarlierDuplicateToastSlot: PropTypes.bool.isRequired,
  stealFocusByDefault: PropTypes.bool,
  onBecameFocused: PropTypes.func,
  toastUnavailable: PropTypes.bool,
};

function HomeBannerRowContent({
  homeCarouselId,
  onHomeFetched,
  homeBannerFetchEnabled,
  registerCommunicationUnavailable,
  stealFocusByDefault = false,
  onBecameFocused = undefined,
}) {
  return (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <HomeBannerSlot
        carouselId={homeCarouselId}
        focusKey={`home-banner-${homeCarouselId}`}
        fetchEnabled={homeBannerFetchEnabled}
        onFetched={onHomeFetched}
        onUnavailable={registerCommunicationUnavailable}
        stealFocusByDefault={stealFocusByDefault}
        onBecameFocused={onBecameFocused}
      />
    </ScrollIntoViewIfFocused>
  );
}

HomeBannerRowContent.propTypes = {
  homeCarouselId: PropTypes.string.isRequired,
  onHomeFetched: PropTypes.func.isRequired,
  homeBannerFetchEnabled: PropTypes.bool.isRequired,
  registerCommunicationUnavailable: PropTypes.func.isRequired,
  onBecameFocused: PropTypes.func,
  stealFocusByDefault: PropTypes.bool,
};

function LanderRow({ index }) {
  const {
    pageId,
    sourceProvider,
    carouselsInfo,
    noFeatured,
    noTitle,
    goToSidebar,
    scrolled,
    fetchedCarousels,
    handleCarouselFetchComplete,
    updatePositionCarousel,
    updateTranslate,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndex,
    updateWasCarouselFocused,
    onTopCarouselFocusKeyChange,
    registerCommunicationUnavailable,
    topToastFocusCarouselId,
    unavailableCommunicationIds,
  } = useItemData();
  const isHomeLander = pageId === PageType.HOME;
  const carouselVerticalPosition = useMemo(() => index + 1, [index]);
  const carouselInfo = useMemo(() => carouselsInfo.get(index), [index, carouselsInfo]);
  const [initialPosition] = useState(() => getPositionsList()[index]);
  const [initialTranslate] = useState(() => getTranslateValuesList()[index]);
  const avoidFetch = useMemo(
    () => fetchedCarousels.has(carouselInfo?.sourceCarouselId || carouselInfo?.elementId),
    [carouselInfo?.sourceCarouselId, carouselInfo?.elementId, fetchedCarousels],
  );
  const isCurableBannerActive = getIsCurableBannerActive();

  // It should be removed from the code when pages v4 is the final version.
  const entityType = isCurableBannerActive
    ? EntityName.BANNER
    : BannerType.UPSELL_BANNER;

  const handlePositionCarouselChange = useCallback((newPositionCarousel) => {
    updatePositionCarousel(index, newPositionCarousel);
  }, [index, updatePositionCarousel]);

  const handleTranslateChange = useCallback((translate) => {
    updateTranslate(index, translate);
  }, [index, updateTranslate]);

  const setInitialSectionIndex = useCallback((shouldBeCero) => {
    sessionStorage.setItem('initialSectionIndex', index);
    if (shouldBeCero) {
      sessionStorage.setItem('offsetFromStartedPage', 0);
    }
  }, [index]);

  const handleCarouselBecameFocused = useCallback(() => {
    setInitialSectionIndex();
    updateWasCarouselFocused();
  }, [setInitialSectionIndex, updateWasCarouselFocused]);

  const handleClickSection = useCallback((shouldBeCero = false) => {
    setInitialSectionIndex(shouldBeCero);
    sessionStorage.setItem('lastViewedCarousel', carouselInfo.elementId);
    sessionStorage.setItem('initialSectionIndexEpg', 0);
  }, [setInitialSectionIndex, carouselInfo]);

  const handleFetchComplete = useCallback((id, carousel) => {
    handleCarouselFetchComplete(id, carousel, index);
  }, [handleCarouselFetchComplete, index]);

  const bannerCarouselId = getCarouselRowBannerId(carouselInfo);

  const firstToastBannerCarouselId = useMemo(() => {
    if (!carouselsInfo?.size) return '';
    for (let i = 0; i < carouselsInfo.size; i += 1) {
      const c = carouselsInfo.get(i);
      if (isToastBannerRowType(readCarouselRowType(c))) {
        return getCarouselRowBannerId(c);
      }
    }
    return '';
  }, [carouselsInfo]);

  const skipEarlierDuplicateToastSlot = useMemo(() => {
    const normThis = String(bannerCarouselId ?? '').trim();
    if (!normThis) return false;
    for (let i = 0; i < index; i += 1) {
      const c = carouselsInfo.get(i);
      if (!isToastBannerRowType(readCarouselRowType(c))) continue;
      if (String(getCarouselRowBannerId(c) ?? '').trim() === normThis) return true;
    }
    return false;
  }, [bannerCarouselId, carouselsInfo, index]);

  const prevCarouselInfo = carouselsInfo.get(index - 1);
  const nextCarouselInfo = carouselsInfo.get(index + 1);

  const toastFetchEnabledThisRow = useMemo(
    () => isHomeLander && isCommunicationBannerFetchEnabledForRowType(
      readCarouselRowType(carouselInfo),
      isToastBannerActive,
      isRowAdsBannerActive,
    ),
    [carouselInfo, isHomeLander],
  );

  const toastFetchEnabledForNextRow = useMemo(
    () => isHomeLander && isCommunicationBannerFetchEnabledForRowType(
      readCarouselRowType(nextCarouselInfo),
      isToastBannerActive,
      isRowAdsBannerActive,
    ),
    [nextCarouselInfo, isHomeLander],
  );

  const homeBannerCommunicationEnabled = isHomeLander && isLanderBannerActive;

  const handleHomeBannerFetched = useCallback((assets) => {
    handleFetchComplete(bannerCarouselId, {
      sourceCarouselId: bannerCarouselId,
      contents: Set(assets || []),
    });
  }, [bannerCarouselId, handleFetchComplete]);

  const nextBannerCarouselId = getCarouselRowBannerId(nextCarouselInfo);
  const handleNextHomeBannerFetched = useCallback((assets) => {
    handleFetchComplete(nextBannerCarouselId, {
      sourceCarouselId: nextBannerCarouselId,
      contents: Set(assets || []),
    });
  }, [nextBannerCarouselId, handleFetchComplete]);

  /* `initialSectionIndex` is undefined when something else owns the page's
     initial focus (an EPG card being restored after Back, a parental return,
     a carousel the user already focused). The hero's own "page at the top"
     auto-focus fires when its data lands, so without this it raced the EPG
     card restore and won whenever the EPG was reachable without scrolling.
     A saved row below the hero (returning to the page after Back) owns the
     initial focus the same way: the sidebar resets it to 0 on every page
     switch, so it is only above 0 when coming back to the same page. */
  const suppressFeaturedAutoFocus = Boolean(topToastFocusCarouselId)
    || initialSectionIndex === undefined
    || initialSectionIndex > 0;

  if (isToastBannerRowType(readCarouselRowType(carouselInfo))) {
    return (
      <ToastBannerRowContent
        bannerCarouselId={bannerCarouselId}
        prevCarouselInfo={prevCarouselInfo}
        nextCarouselInfo={nextCarouselInfo}
        nextBannerCarouselId={nextBannerCarouselId}
        onToastFetched={handleHomeBannerFetched}
        onNextHomeFetched={handleNextHomeBannerFetched}
        toastFetchEnabled={toastFetchEnabledThisRow}
        homeBannerFetchEnabled={homeBannerCommunicationEnabled}
        registerCommunicationUnavailable={registerCommunicationUnavailable}
        skipEarlierDuplicateToastSlot={skipEarlierDuplicateToastSlot}
        stealFocusByDefault={index === initialSectionIndex}
        onBecameFocused={handleCarouselBecameFocused}
        toastUnavailable={unavailableCommunicationIds.has(bannerCarouselId)}
      />
    );
  }

  if (isHomeBannerRowType(readCarouselRowType(carouselInfo))) {
    return (
      <HomeBannerRowContent
        homeCarouselId={bannerCarouselId}
        prevCarouselInfo={prevCarouselInfo}
        nextCarouselInfo={nextCarouselInfo}
        firstToastBannerCarouselId={firstToastBannerCarouselId}
        onHomeFetched={handleHomeBannerFetched}
        onNextToastFetched={handleNextHomeBannerFetched}
        toastFetchEnabled={toastFetchEnabledForNextRow}
        homeBannerFetchEnabled={homeBannerCommunicationEnabled}
        registerCommunicationUnavailable={registerCommunicationUnavailable}
        stealFocusByDefault={index === initialSectionIndex}
        onBecameFocused={handleCarouselBecameFocused}
      />
    );
  }

  return carouselInfo.type === entityType ? (
    <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
      <UpSellBanner elementId={isCurableBannerActive ? carouselInfo?.elementId : undefined} />
    </ScrollIntoViewIfFocused>
  ) : (
    <ScrollIntoViewIfFocused
      position={ScrollPosition.CENTER}
      /* Height 100% of the virtualized row div: the measured layout always
         matches the row slot, so geometry stays stable across the
         skeleton → carousel transition (rows never overlap). */
      style={{ height: '100%' }}
    >
      <SwimlaneCarouselContainer
        carouselInfo={carouselInfo}
        noFeatured={noFeatured}
        noTitle={noTitle}
        pageId={pageId}
        sourceProvider={sourceProvider}
        avoidFetch={avoidFetch}
        initialPosition={initialPosition}
        initialTranslate={initialTranslate}
        stealFocusByDefault={index === initialSectionIndex}
        suppressFeaturedAutoFocus={suppressFeaturedAutoFocus}
        scrolled={scrolled}
        onFetchComplete={handleFetchComplete}
        onNoMoreLeftElements={goToSidebar}
        onPositionCarouselChange={handlePositionCarouselChange}
        onTranslateChange={handleTranslateChange}
        onBecameFocused={handleCarouselBecameFocused}
        handleClickSection={handleClickSection}
        carouselVerticalPosition={carouselVerticalPosition}
        onSetFocusKey={index === 0 ? onTopCarouselFocusKeyChange : undefined}
      />
    </ScrollIntoViewIfFocused>
  );
}

LanderRow.propTypes = {
  index: PropTypes.number.isRequired,
};

export default memo(LanderRow);
